import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { FileCheck, Sparkles, User, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Shortlists() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/shortlists");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const open = async (id) => {
    const { data } = await api.get(`/shortlists/${id}`);
    setSel(data); setBrief(null);
  };

  const genBrief = async (cid) => {
    setBusy(true); setBrief({loading: true, candidate_id: cid});
    try {
      const { data } = await api.post(`/candidates/${cid}/brief`);
      setBrief(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Brief failed"); setBrief(null); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Client Shortlists</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Presented candidates</h1>
        <p className="text-sm text-slate-500 mt-1">3-4 highly qualified finalists per open role — with client-safe candidate briefs.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest font-semibold">
            <tr>
              <th className="text-left px-4 py-3">Shortlist</th>
              <th className="text-left px-4 py-3">Candidates</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} data-testid={`shortlist-row-${r.id}`} onClick={()=>open(r.id)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3 font-semibold text-slate-900">{r.name || `Shortlist ${r.id.slice(0,8)}`}</td>
                <td className="px-4 py-3 font-mono">{(r.candidate_ids || []).length}</td>
                <td className="px-4 py-3"><span className="text-[10px] font-semibold px-2 py-0.5 border border-slate-200 rounded">{r.status}</span></td>
                <td className="px-4 py-3 text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="text-center py-12 text-sm text-slate-500">No shortlists yet. Create one from the AI Matching page.</td></tr>}
          </tbody>
        </table>
      </div>

      {sel && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 flex justify-end" onClick={()=>setSel(null)}>
          <div onClick={e=>e.stopPropagation()} data-testid="shortlist-drawer" className="w-full max-w-2xl bg-white h-full overflow-y-auto p-6 shadow-xl">
            <h3 className="font-display font-bold text-xl text-slate-900 mb-1">{sel.name || "Shortlist"}</h3>
            <p className="text-xs text-slate-500 mb-4">{sel.candidates?.length || 0} candidates · {sel.status}</p>
            <div className="space-y-3">
              {(sel.candidates || []).map(c => (
                <div key={c.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center border border-sky-100"><User size={14}/></div>
                      <div>
                        <div className="font-semibold text-slate-900">{c.first_name} {c.last_name}</div>
                        <div className="text-[11px] text-slate-500">{c.primary_role} · {c.country} · {c.qualification_tier || "Applicant"}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Link data-testid={`emp-view-${c.id}`} to={`/employer/candidates/${c.id}?job=${sel.job_order_id||""}`} className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"><Eye size={12}/> Employer view</Link>
                      <button data-testid={`brief-btn-${c.id}`} onClick={()=>genBrief(c.id)} disabled={busy} className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"><Sparkles size={12}/> Generate brief</button>
                    </div>
                  </div>
                  <div className="text-xs text-slate-600 line-clamp-2">{c.summary || c.headline}</div>
                  {brief && brief.candidate_id === c.id && (
                    <div className="mt-3 p-3 rounded bg-sky-50 border border-sky-200 text-xs">
                      {brief.loading ? <div className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Generating…</div> :
                        <div className="space-y-2">
                          {["why_recommend","professional_summary","top_skills","relevant_experience","education_credentials","languages_communication","verified_accomplishments","availability_compensation","potential_gaps"].map(k => brief[k] && (
                            <div key={k}>
                              <div className="text-[10px] uppercase tracking-widest font-semibold text-sky-900 mb-0.5">{k.replace(/_/g," ")}</div>
                              <div className="text-slate-800">{Array.isArray(brief[k]) ? brief[k].join(" · ") : brief[k]}</div>
                            </div>
                          ))}
                        </div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
